// ============================================================================
// ClientPortalShowcase — the landing section that sells the CLIENT-facing portal
// (the mobile-first PWA each invited client gets): an animated dashboard, their
// nutrition, this week's goals, 1:1 messaging, a colour-coded training calendar,
// and their progress report. Anchored at #portal. Carries a prominent CLIENT
// login entry (/me/login) — the second of the two clear login doorways the
// landing must present (the trainer login lives in the nav / hero / CTA).
//
// Reveals on scroll, staggered, reduced-motion-aware. Reuses the locked Pulse
// Card + Button + gradient/shadow tokens. EN + 繁中 via i18n keys only.
// ============================================================================
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Button, Card } from "../../components";
import { CLIENT_LOGIN_PATH } from "../../client/routes";
import type { ReactNode } from "react";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

function ArrowRight() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRings() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" opacity="0.4" />
      <path d="M12 3.5a8.5 8.5 0 0 1 7 13.3" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
function IconApple() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path
        d="M12 8c-1.6-1.8-5.4-1.8-6.6 1.2-1.2 3 1 7.4 3.4 8.8 1.4.8 1.6.8 3 0 2.4-1.4 4.6-5.8 3.4-8.8C17.4 6.2 13.6 6.2 12 8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M12 8c0-2 1-3.4 3-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconTarget() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3.4" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
function IconChat() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path
        d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconCal() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M3.5 9.5h17M8 3v3M16 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconReport() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path d="M5 19V5h10l4 4v10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M8 13l2.5 2.5L16 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface PortalItem {
  key: "dashboard" | "nutrition" | "goals" | "messaging" | "calendar" | "reports";
  icon: ReactNode;
  tint: string;
}

const ITEMS: PortalItem[] = [
  { key: "dashboard", icon: <IconRings />, tint: "bg-coral-soft text-coral" },
  { key: "nutrition", icon: <IconApple />, tint: "bg-lime-soft text-lime-600" },
  { key: "goals", icon: <IconTarget />, tint: "bg-violet-soft text-violet" },
  { key: "messaging", icon: <IconChat />, tint: "bg-ocean-soft text-ocean-600" },
  { key: "calendar", icon: <IconCal />, tint: "bg-amber-soft text-volt-600" },
  { key: "reports", icon: <IconReport />, tint: "bg-coral-soft text-coral" },
];

export function ClientPortalShowcase() {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  return (
    <section
      id="portal"
      className="relative scroll-mt-20 bg-paper py-16 sm:py-20 lg:py-24"
    >
      <div className="mx-auto max-w-shell px-gutter">
        <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
          {/* ---- Copy + dual entry ---- */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="lg:sticky lg:top-24"
          >
            <p className="font-display text-sm font-bold uppercase tracking-kicker text-coral">
              {t("portal.kicker")}
            </p>
            <h2 className="mt-3 font-display font-extrabold tracking-tightest text-body text-[clamp(1.9rem,1.2rem+3vw,3rem)]">
              {t("portal.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-body-muted sm:text-lg">
              {t("portal.subtitle")}
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to={CLIENT_LOGIN_PATH} className="sm:w-auto">
                <Button size="lg" block iconRight={<ArrowRight />}>
                  {t("portal.cta")}
                </Button>
              </Link>
              <span className="text-sm font-medium text-body-faint">
                {t("portal.ctaNote")}
              </span>
            </div>
          </motion.div>

          {/* ---- Capability grid ---- */}
          <motion.div
            variants={reduce ? undefined : container}
            initial={reduce ? undefined : "hidden"}
            whileInView={reduce ? undefined : "show"}
            viewport={{ once: true, amount: 0.15 }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {ITEMS.map(({ key, icon, tint }) => (
              <motion.div key={key} variants={reduce ? undefined : rise} className="h-full">
                <Card interactive className="flex h-full gap-4">
                  <span className={"grid h-12 w-12 shrink-0 place-items-center rounded-md " + tint}>
                    {icon}
                  </span>
                  <div>
                    <h3 className="font-display text-base font-bold text-body">
                      {t(`portal.items.${key}.title`)}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-body-muted">
                      {t(`portal.items.${key}.body`)}
                    </p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
